/**
 * AccountAlertFormModal — add / edit one of the account's alerts (Admin >
 * Account > Account Alerts): the corporate dashboard's Add / Edit Alert dialog
 * (corp-dashboard/components/account-alerts-table.tsx) for one account, its
 * 1200px width and its order:
 *
 *   - on top, held outside the scrolling fields (the Modal's `pinned`): the
 *     card as the dashboard will show it (the dashboard's own AlertHeroCard,
 *     so the preview cannot drift);
 *   - left (w-72): the one Image, filling the column, beside the copy or as
 *     the card background ("Use as card background"), and Appearance —
 *     background colour, gradient end and direction, text colour, overlay
 *     strength and coverage;
 *   - right: Title * (bold / italic / underline and colour on the selection,
 *     a font for the whole title); User Level, Display Order, Start and Stop
 *     Date on the account's own clock; Banner URL; the Button (text, URL,
 *     position, colours); Content / Description and More Details in corp's
 *     editor (RichTextEditor).
 *   - corp's required strip (title, hex colours) and suggestions (a button
 *     with no link, a background image with no colour).
 *
 * Dates are stored on Pacific time, as corp stores them, and shown on the
 * account's timezone. A start date picked fresh starts at 00:00 and a stop
 * date ends at 23:59, as corp's did. An edit sends only what changed.
 * Corp's account picker and compare / clone tools are multi-account: left out.
 *
 * On a Locked page AdminFormModal makes it a viewer and its field lock covers
 * the shared inputs; this form also drops what that lock cannot reach, with
 * nothing saying why (R18): the title's B / I / U and colour controls (the
 * title itself is not editable, see AlertTitleInput), every native colour
 * picker (a plain swatch shows the colour), Clear and Remove photo.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { Bold, Italic, Trash, Underline } from 'lucide-react';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AlertTitleInput, execTitleCommand, rememberTitleSelection } from '@/components/admin/AlertTitleInput';
import { DESCRIPTION_CLASS, DialogField, LABEL_CLASS, ModifiedPill, fieldClasses } from '@/components/admin/DialogChrome';
import { EntityImageTile } from '@/components/admin/EntityImageTile';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { AlertHeroCard } from '@/components/dashboard/AlertHeroCard';
import { Button } from '@/components/ui/Button';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';
import {
  ALERT_BG_DIRECTIONS,
  ALERT_BUTTON_POSITIONS,
  ALERT_TITLE_FONTS,
  DEFAULT_BG_COLOR,
  DEFAULT_BUTTON_COLOR,
  hasRichTextContent,
  isHexColor,
  normalizeHexInput,
  richTextToPlain,
} from '@/lib/alertHeroStyle';
import { getTimezoneAbbreviation, reprojectDateTime } from '@/lib/facilityTimezone';
import { getErrorMessage } from '@/services/apiClient';
import { createAccountAlert, updateAccountAlert, type AccountAlert, type AccountAlertInput } from '@/services/accountAlerts.service';
import { toast } from '@/store/toastStore';

// ─── Form ──────────────────────────────────────────────────────────────────────

export interface AlertForm {
  alertTitle: string;
  alertURL: string;
  alertContent: string;
  moreDetails: string;
  alertPhoto: string;
  bgImage: string;
  bgColor: string;
  bgColorEnd: string;
  bgDirection: string;
  bgOverlayOpacity: string;
  bgOverlayCoverage: string;
  textColor: string;
  titleFont: string;
  buttonText: string;
  buttonURL: string;
  buttonColor: string;
  buttonTextColor: string;
  buttonPosition: string;
  userLevel: number;
  /** '' = unranked (newest first). */
  alertOrder: string;
  /** 'YYYY-MM-DDTHH:mm' on the account's clock, or '' for none. */
  startDate: string;
  stopDate: string;
}

export const PACIFIC_TZ = 'America/Los_Angeles';
const ZERO_DATE = '0000-00-00 00:00:00';

/** A stored Pacific 'YYYY-MM-DD HH:MM:SS' on the account's clock ('YYYY-MM-DDTHH:mm'), '' for none. */
export function storedToLocal(stored: string, tz: string): string {
  if (!stored || stored.startsWith('0000-00-00')) return '';
  const [date, time = '00:00'] = stored.split(/[ T]/);
  const r = reprojectDateTime(date, time.slice(0, 5), PACIFIC_TZ, tz || PACIFIC_TZ);
  return `${r.date}T${r.time}`;
}

/** Back to stored Pacific time. A stop date covers its whole minute (:59), as corp's did. */
export function localToStored(local: string, tz: string, edge: 'start' | 'stop'): string {
  if (!local) return ZERO_DATE;
  const [date, time = '00:00'] = local.split('T');
  const r = reprojectDateTime(date, time.slice(0, 5), tz || PACIFIC_TZ, PACIFIC_TZ);
  return `${r.date} ${r.time}:${edge === 'stop' ? '59' : '00'}`;
}

export function alertFormFrom(a: AccountAlert | null, tz: string): AlertForm {
  return {
    alertTitle: a?.alertTitle ?? '',
    alertURL: a?.alertURL ?? '',
    alertContent: a?.alertContent ?? '',
    moreDetails: a?.moreDetails ?? '',
    alertPhoto: a?.alertPhoto ?? '',
    bgImage: a?.bgImage ?? '',
    bgColor: a?.bgColor ?? '',
    bgColorEnd: a?.bgColorEnd ?? '',
    bgDirection: a?.bgDirection ?? '',
    bgOverlayOpacity: a?.bgOverlayOpacity ?? '',
    bgOverlayCoverage: a?.bgOverlayCoverage ?? '',
    textColor: a?.textColor ?? '',
    titleFont: a?.titleFont ?? '',
    buttonText: a?.buttonText ?? '',
    buttonURL: a?.buttonURL ?? '',
    buttonColor: a?.buttonColor ?? '',
    buttonTextColor: a?.buttonTextColor ?? '',
    buttonPosition: a?.buttonPosition ?? '',
    userLevel: a?.userLevel ?? 0,
    alertOrder: a && a.alertOrder > 0 ? String(a.alertOrder) : '',
    startDate: a ? storedToLocal(a.startDate, tz) : '',
    stopDate: a ? storedToLocal(a.stopDate, tz) : '',
  };
}

/** The API's fields. `only` limits it to the changed ones (an edit). */
export function alertPayload(f: AlertForm, tz: string, only?: Set<keyof AlertForm>): Partial<AccountAlertInput> {
  const all: AccountAlertInput = {
    alertTitle: f.alertTitle,
    alertURL: f.alertURL.trim(),
    alertContent: f.alertContent,
    moreDetails: f.moreDetails,
    alertPhoto: f.alertPhoto,
    bgImage: f.bgImage,
    bgColor: f.bgColor,
    bgColorEnd: f.bgColorEnd,
    bgDirection: f.bgDirection,
    bgOverlayOpacity: f.bgOverlayOpacity,
    bgOverlayCoverage: f.bgOverlayCoverage,
    textColor: f.textColor,
    titleFont: f.titleFont,
    buttonText: f.buttonText.trim(),
    buttonURL: f.buttonURL.trim(),
    buttonColor: f.buttonColor,
    buttonTextColor: f.buttonTextColor,
    buttonPosition: f.buttonPosition,
    userLevel: f.userLevel,
    alertOrder: Number(f.alertOrder) || 0,
    startDate: localToStored(f.startDate, tz, 'start'),
    stopDate: localToStored(f.stopDate, tz, 'stop'),
  };
  if (!only) return all;
  const out: Partial<AccountAlertInput> = {};
  const src = all as unknown as Record<string, unknown>;
  for (const key of only) {
    if (key in src) (out as Record<string, unknown>)[key] = src[key];
  }
  return out;
}

const COLOR_FIELDS: [keyof AlertForm, string][] = [
  ['bgColor', 'Background color'],
  ['bgColorEnd', 'Gradient end color'],
  ['textColor', 'Text color'],
  ['buttonColor', 'Button color'],
  ['buttonTextColor', 'Button text color'],
];

/** Corp's checks: required (blocks saving) and suggestions. */
export function alertChecks(f: AlertForm): { errors: string[]; suggestions: string[] } {
  const errors: string[] = [];
  if (!hasRichTextContent(f.alertTitle)) errors.push('Title is required');
  for (const [field, label] of COLOR_FIELDS) {
    const v = String(f[field] ?? '');
    if (v && !isHexColor(v)) errors.push(`${label} must be a 6-digit hex like #2563eb`);
  }
  const suggestions: string[] = [];
  if (f.buttonURL.trim() && !f.buttonText.trim()) suggestions.push('Button URL is set but the button has no text, so no button will show');
  if (f.buttonText.trim() && !f.buttonURL.trim() && !f.alertURL.trim()) {
    suggestions.push('The button has no link — add a Button URL, or a Banner URL for it to fall back to');
  }
  if (f.bgImage.trim() && !f.bgColor.trim()) suggestions.push('Set a background color too — it tints the shade drawn over the background image');
  return { errors, suggestions };
}

const USER_LEVEL_OPTIONS = [
  { value: 0, label: 'All Users (Level 0)' },
  { value: 1, label: 'Level 1+' },
  { value: 2, label: 'Level 2+' },
  { value: 3, label: 'Level 3+' },
  { value: 4, label: 'Level 4+' },
  { value: 5, label: 'Level 5+' },
];

const digits = (v: string, max: number) => v.replace(/[^0-9]/g, '').slice(0, max);

// ─── Modal ─────────────────────────────────────────────────────────────────────

export interface AccountAlertFormModalProps {
  /** The alert to edit; null to add one. */
  alert: AccountAlert | null;
  /** The account's IANA timezone (dates are shown on it). */
  timezone: string;
  onClose: () => void;
  onSaved: () => void;
}

export function AccountAlertFormModal({ alert, timezone: openedTz, onClose, onSaved }: AccountAlertFormModalProps) {
  const isNew = alert === null;
  const locked = useAdminPageLevel() === 'locked';
  // The clock the dates were shown on is the one they are saved from, even if
  // the account's timezone finishes loading while the form is open.
  const [timezone] = useState(openedTz);
  const t = useTrackedForm<AlertForm>(() => alertFormFrom(alert, timezone), { tracking: !isNew });
  const f = t.form;
  const [saving, setSaving] = useState(false);
  // One image serves the card; this decides whether it sits beside the copy
  // (alertPhoto) or fills the background (bgImage).
  const [asBackground, setAsBackground] = useState(() => !!alert?.bgImage || !alert?.alertPhoto);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const [titleCmds, setTitleCmds] = useState({ bold: false, italic: false, underline: false });

  // Light up B / I / U for whatever the caret is on.
  useEffect(() => {
    const onSelectionChange = () => {
      const el = titleRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0 || !el.contains(sel.getRangeAt(0).commonAncestorContainer)) return;
      rememberTitleSelection(el);
      setTitleCmds({
        bold: document.queryCommandState?.('bold') ?? false,
        italic: document.queryCommandState?.('italic') ?? false,
        underline: document.queryCommandState?.('underline') ?? false,
      });
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  const { errors, suggestions } = alertChecks(f);
  const tzAbbr = useMemo(() => getTimezoneAbbreviation(timezone || PACIFIC_TZ), [timezone]);
  const imageUrl = f.bgImage || f.alertPhoto;

  /** The card as the dashboard draws it, from the form. */
  const preview: AccountAlert = {
    accountAlertID: alert?.accountAlertID ?? 0,
    alertTitle: hasRichTextContent(f.alertTitle) ? f.alertTitle : 'Announcement title',
    alertURL: f.alertURL,
    alertContent: f.alertContent,
    moreDetails: f.moreDetails,
    alertPhoto: f.alertPhoto,
    videoURL: alert?.videoURL ?? '',
    status: alert?.status ?? 0,
    userLevel: f.userLevel,
    startDate: ZERO_DATE,
    stopDate: ZERO_DATE,
    alertOrder: Number(f.alertOrder) || 0,
    active: true,
    createDate: '',
    endDate: ZERO_DATE,
    dismissedAt: null,
    accountID: alert?.accountID ?? 0,
    masterAccountID: null,
    isBroadcast: false,
    bgColor: f.bgColor,
    bgColorEnd: f.bgColorEnd,
    bgDirection: f.bgDirection,
    bgImage: f.bgImage,
    bgOverlayOpacity: f.bgOverlayOpacity,
    bgOverlayCoverage: f.bgOverlayCoverage,
    textColor: f.textColor,
    titleFont: f.titleFont,
    buttonText: f.buttonText,
    buttonURL: f.buttonURL,
    buttonColor: f.buttonColor,
    buttonTextColor: f.buttonTextColor,
    buttonPosition: f.buttonPosition,
  };

  function setImageTarget(background: boolean) {
    setAsBackground(background);
    const url = f.bgImage || f.alertPhoto;
    t.patch({ bgImage: background ? url : '', alertPhoto: background ? '' : url });
  }

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      if (isNew) {
        await createAccountAlert({ ...(alertPayload(f, timezone) as AccountAlertInput), status: 0 });
      } else {
        const payload = alertPayload(f, timezone, t.changed);
        if (Object.keys(payload).length > 0) await updateAccountAlert(alert.accountAlertID, payload);
      }
      toast.success(`"${richTextToPlain(f.alertTitle)}" ${isNew ? 'created' : 'updated'}`);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The alert could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const imageChanged = t.changed.has('alertPhoto') || t.changed.has('bgImage');
  const titleMissing = !hasRichTextContent(f.alertTitle);

  return (
    <AdminFormModal
      title={isNew ? 'Add Alert' : 'Edit Alert'}
      isNew={isNew}
      dirty={isNew ? t.changed.size > 0 : t.dirty}
      saving={saving}
      errors={errors}
      suggestions={suggestions}
      onSave={() => void save()}
      onRevertAll={() => {
        t.revertAll();
        setAsBackground(!!t.saved.bgImage || !t.saved.alertPhoto);
      }}
      onClose={onClose}
      size="xxl"
      // Corp's 1200px dialog.
      panelClassName="md:max-w-[1200px]"
      // Held above the scrolling fields, as corp's sits outside its scroll area.
      pinned={
        <div data-testid="alert-preview">
          <AlertHeroCard alert={preview} dateLabel="Posted today" />
        </div>
      }
    >
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left: the image and how the card looks */}
        <div className="space-y-6 lg:w-72 lg:shrink-0">
          <div className="space-y-2">
            <span className={LABEL_CLASS}>
              <span>Image</span>
              {imageChanged && (
                <ModifiedPill
                  label="Image"
                  onRevert={() => {
                    t.patch({ alertPhoto: t.saved.alertPhoto, bgImage: t.saved.bgImage });
                    setAsBackground(!!t.saved.bgImage || !t.saved.alertPhoto);
                  }}
                />
              )}
            </span>
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="alert-image-background" className={cn(locked ? 'cursor-default' : 'cursor-pointer', 'text-xs text-slate-600')}>
                Use as card background
              </label>
              <Switch id="alert-image-background" checked={asBackground} onChange={setImageTarget} aria-label="Use as card background" />
            </div>
            <EntityImageTile
              entityType="account-alert"
              entityId={null}
              folder="alerts"
              imageUrl={imageUrl}
              label=""
              fullWidth
              onSelected={(url) => t.patch(asBackground ? { bgImage: url, alertPhoto: '' } : { alertPhoto: url, bgImage: '' })}
            />
            {imageUrl && !locked && (
              <Button
                variant="ghost"
                size="xs"
                className="h-7 w-full text-xs text-slate-500 hover:text-red-600"
                leftIcon={<Trash className="h-3.5 w-3.5" />}
                onClick={() => t.patch({ alertPhoto: '', bgImage: '' })}
              >
                Remove photo
              </Button>
            )}
          </div>

          <section className="space-y-4 border-t border-slate-200 pt-4" aria-label="Appearance">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Appearance</h4>
              <p className={DESCRIPTION_CLASS}>Blank = the default dark gradient.</p>
            </div>
            <ColorField
              id="alert-bg-color"
              label="Background color"
              value={f.bgColor}
              placeholder={DEFAULT_BG_COLOR}
              hint="First gradient stop; also tints a background image"
              badge={t.badge('bgColor', 'Background color')}
              onChange={(v) => t.patch({ bgColor: v })}
            />
            <ColorField
              id="alert-bg-color-end"
              label="Gradient end color"
              value={f.bgColorEnd}
              hint="Blank gives a solid background"
              badge={t.badge('bgColorEnd', 'Gradient end color')}
              onChange={(v) => t.patch({ bgColorEnd: v })}
            />
            <DialogField label="Gradient direction" htmlFor="alert-bg-direction" description="Only used when an end color is set." badge={t.badge('bgDirection', 'Gradient direction')}>
              <Select<string>
                id="alert-bg-direction"
                options={ALERT_BG_DIRECTIONS.map((d) => ({ value: d.value as string, label: d.label }))}
                value={f.bgDirection || ALERT_BG_DIRECTIONS[0].value}
                onChange={(v) => v && t.patch({ bgDirection: v })}
              />
            </DialogField>
            <ColorField
              id="alert-text-color"
              label="Text color"
              value={f.textColor}
              hint="Blank picks black or white automatically"
              badge={t.badge('textColor', 'Text color')}
              onChange={(v) => t.patch({ textColor: v })}
            />
            <DialogField
              label="Overlay strength"
              htmlFor="alert-overlay-opacity"
              description="0–100. How opaque the shade over a background image is. At 100 the image is hidden completely."
              badge={t.badge('bgOverlayOpacity', 'Overlay strength')}
            >
              <Input
                id="alert-overlay-opacity"
                inputMode="numeric"
                value={f.bgOverlayOpacity}
                placeholder="90"
                onChange={(e) => t.patch({ bgOverlayOpacity: digits(e.target.value, 3) })}
              />
            </DialogField>
            <DialogField
              label="Overlay coverage"
              htmlFor="alert-overlay-coverage"
              description="0–100. How much of the card stays at full strength before fading out. At 100 the whole card is shaded."
              badge={t.badge('bgOverlayCoverage', 'Overlay coverage')}
            >
              <Input
                id="alert-overlay-coverage"
                inputMode="numeric"
                value={f.bgOverlayCoverage}
                placeholder="25"
                onChange={(e) => t.patch({ bgOverlayCoverage: digits(e.target.value, 3) })}
              />
            </DialogField>
          </section>
        </div>

        {/* Right: content, audience and schedule, links, button */}
        <div className="min-w-0 flex-1 space-y-6">
          <DialogField
            label="Title"
            htmlFor="alert-title"
            required
            state={titleMissing ? 'error' : 'none'}
            labelHint={titleMissing ? '— required' : undefined}
            badge={t.badge('alertTitle', 'Title')}
          >
            <AlertTitleInput id="alert-title" innerRef={titleRef} value={f.alertTitle} onChange={(html) => t.patch({ alertTitle: html })} placeholder="Alert title" invalid={titleMissing} />
            {/* B / I / U and colour apply to the selection; the font to the whole title. */}
            <div className="flex flex-wrap items-center gap-3">
              {!locked && (
                <>
                  <div className="flex overflow-hidden rounded-md border border-slate-200">
                    {(
                      [
                        ['bold', Bold, 'Bold'],
                        ['italic', Italic, 'Italic'],
                        ['underline', Underline, 'Underline'],
                      ] as const
                    ).map(([cmd, Icon, name], i) => (
                      <button
                        key={cmd}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => execTitleCommand(titleRef.current, cmd)}
                        aria-pressed={titleCmds[cmd]}
                        aria-label={`${name} (selection)`}
                        title={`${name} (selection)`}
                        className={cn('px-2.5 py-2 hover:bg-slate-50', i > 0 && 'border-l border-slate-200', titleCmds[cmd] ? 'bg-blue-50 text-blue-700' : 'text-slate-500')}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </button>
                    ))}
                  </div>
                  {/* No preventDefault here — it would stop the picker opening — so the selection is stashed first. */}
                  <input
                    type="color"
                    aria-label="Title selection color"
                    title="Color (selection)"
                    onMouseDown={() => rememberTitleSelection(titleRef.current)}
                    onChange={(e) => execTitleCommand(titleRef.current, 'foreColor', e.target.value)}
                    className="h-9 w-10 shrink-0 cursor-pointer rounded border border-slate-200 bg-transparent p-0.5"
                  />
                </>
              )}
              <div className="flex items-center gap-1.5">
                <label htmlFor="alert-title-font" className="sr-only">
                  Title font
                </label>
                <Select<string>
                  id="alert-title-font"
                  wrapperClassName="w-40"
                  options={ALERT_TITLE_FONTS.map((font) => ({ value: font.value as string, label: font.label }))}
                  renderOption={(o) => {
                    const css = ALERT_TITLE_FONTS.find((font) => font.value === o.value)?.css;
                    return <span style={css ? { fontFamily: css } : undefined}>{o.label}</span>;
                  }}
                  value={f.titleFont || 'default'}
                  onChange={(v) => t.patch({ titleFont: !v || v === 'default' ? '' : v })}
                />
                {t.badge('titleFont', 'Title font')}
              </div>
            </div>
          </DialogField>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <DialogField label="User Level" htmlFor="alert-user-level" badge={t.badge('userLevel', 'User Level')}>
              <Select<number> id="alert-user-level" options={USER_LEVEL_OPTIONS} value={f.userLevel} onChange={(v) => t.patch({ userLevel: v ?? 0 })} />
            </DialogField>
            <DialogField label="Display Order" htmlFor="alert-order" description="1 shows first; empty = newest first" badge={t.badge('alertOrder', 'Display Order')}>
              <Input id="alert-order" inputMode="numeric" value={f.alertOrder} placeholder="auto" onChange={(e) => t.patch({ alertOrder: digits(e.target.value, 6) })} />
            </DialogField>
            <DialogField label={`Start Date (${tzAbbr})`} htmlFor="alert-start" description="Empty = immediate" badge={t.badge('startDate', 'Start Date')}>
              <DateTimePicker
                id="alert-start"
                ariaLabel="Start Date"
                value={f.startDate}
                // A date picked fresh starts the day, as corp's did.
                onChange={(v) => t.patch({ startDate: v && !f.startDate ? `${v.slice(0, 10)}T00:00` : v })}
              />
            </DialogField>
            <DialogField label={`Stop Date (${tzAbbr})`} htmlFor="alert-stop" description="Empty = indefinite" badge={t.badge('stopDate', 'Stop Date')}>
              <DateTimePicker
                id="alert-stop"
                ariaLabel="Stop Date"
                value={f.stopDate}
                // A date picked fresh runs to the end of the day.
                onChange={(v) => t.patch({ stopDate: v && !f.stopDate ? `${v.slice(0, 10)}T23:59` : v })}
              />
            </DialogField>
          </div>

          <DialogField label="Banner URL" htmlFor="alert-url" description="Where the title links; the button falls back to it." badge={t.badge('alertURL', 'Banner URL')}>
            <Input id="alert-url" value={f.alertURL} placeholder="https://..." onChange={(e) => t.patch({ alertURL: e.target.value })} />
          </DialogField>

          <section className="space-y-4 border-t border-slate-200 pt-4" aria-label="Button">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Button</h4>
              <p className={DESCRIPTION_CLASS}>Only shows once it has text.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <DialogField label="Button text" htmlFor="alert-button-text" badge={t.badge('buttonText', 'Button text')}>
                <Input id="alert-button-text" value={f.buttonText} placeholder="Start now" onChange={(e) => t.patch({ buttonText: e.target.value })} />
              </DialogField>
              <DialogField label="Button URL" htmlFor="alert-button-url" description="Blank falls back to the banner URL." badge={t.badge('buttonURL', 'Button URL')}>
                <Input id="alert-button-url" value={f.buttonURL} placeholder="https://..." onChange={(e) => t.patch({ buttonURL: e.target.value })} />
              </DialogField>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <DialogField label="Button Position" htmlFor="alert-button-position" description="Auto: under the photo, else bottom." badge={t.badge('buttonPosition', 'Button Position')}>
                <Select<string>
                  id="alert-button-position"
                  options={ALERT_BUTTON_POSITIONS.map((p) => ({ value: p.value as string, label: p.label }))}
                  value={f.buttonPosition || 'auto'}
                  onChange={(v) => t.patch({ buttonPosition: !v || v === 'auto' ? '' : v })}
                />
              </DialogField>
              <ColorField
                id="alert-button-color"
                label="Button color"
                value={f.buttonColor}
                placeholder={DEFAULT_BUTTON_COLOR}
                badge={t.badge('buttonColor', 'Button color')}
                onChange={(v) => t.patch({ buttonColor: v })}
              />
              <ColorField
                id="alert-button-text-color"
                label="Button text color"
                value={f.buttonTextColor}
                hint="Blank picks black or white automatically"
                badge={t.badge('buttonTextColor', 'Button text color')}
                onChange={(v) => t.patch({ buttonTextColor: v })}
              />
            </div>
          </section>

          <DialogField label="Content / Description" htmlFor="alert-content" badge={t.badge('alertContent', 'Content / Description')}>
            <RichTextEditor id="alert-content" value={f.alertContent} onChange={(html) => t.patch({ alertContent: html })} placeholder="Alert message content..." />
          </DialogField>

          <DialogField label="More Details" htmlFor="alert-more-details" badge={t.badge('moreDetails', 'More Details')}>
            <RichTextEditor id="alert-more-details" value={f.moreDetails} onChange={(html) => t.patch({ moreDetails: html })} placeholder="Additional details..." />
          </DialogField>
        </div>
      </div>
    </AdminFormModal>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────────

/** Swatch + hex input (+ Clear), red with corp's "— must be #rrggbb" when not a hex. */
function ColorField({
  id,
  label,
  value,
  placeholder,
  hint,
  badge,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  hint?: string;
  badge: ReactNode;
  onChange: (v: string) => void;
}) {
  // Locked: a plain swatch in place of the native picker, and no Clear.
  const locked = useFieldLocked();
  const invalid = !!value && !isHexColor(value);
  const swatch = isHexColor(value) ? value : isHexColor(placeholder) ? (placeholder as string) : '#000000';
  return (
    <DialogField
      label={label}
      htmlFor={id}
      state={invalid ? 'error' : 'none'}
      labelHint={invalid ? '— must be #rrggbb' : undefined}
      description={invalid ? undefined : hint}
      badge={badge}
    >
      <div className="flex items-center gap-2">
        {locked ? (
          <span aria-hidden className="h-9 w-12 shrink-0 rounded border border-slate-200 p-0.5">
            <span className="block h-full w-full rounded-sm" style={{ backgroundColor: swatch }} />
          </span>
        ) : (
          <input
            type="color"
            aria-label={`${label} picker`}
            value={swatch}
            onChange={(e) => onChange(e.target.value.toLowerCase())}
            className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-200 bg-transparent p-0.5"
          />
        )}
        <Input
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(normalizeHexInput(e.target.value))}
          className={fieldClasses.input(invalid ? 'error' : 'none')}
          wrapperClassName="min-w-0 flex-1"
        />
        {!!value && !locked && (
          <Button variant="ghost" size="xs" className="h-9 px-2 text-xs text-slate-500" onClick={() => onChange('')}>
            Clear
          </Button>
        )}
      </div>
    </DialogField>
  );
}
